/** The Surveys API from the app (1.7.3): the list, one survey with its count, the presses, the people search. */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { EventSendResult, InvitePreview } from '@shared/events';
import type { PersonSurvey, SurveyDetail, SurveyInput, SurveysResponse } from '@shared/surveys';
import { apiFetch } from '@/lib/api';

const sid = (slug: string) => encodeURIComponent(slug);
export const surveyPath = (slug: string, tail = '') => `/api/surveys/s/${sid(slug)}${tail}`;

export interface SurveyPerson {
  tenantId: string;
  name: string;
  unit: string;
  email: string;
}

export function useSurveys() {
  return useQuery({ queryKey: ['surveys'], queryFn: () => apiFetch<SurveysResponse>('/api/surveys'), staleTime: 20_000, refetchOnWindowFocus: true });
}

/** Live: the count moves while people answer (re-read every 15 seconds while the page is open). */
export function useSurvey(slug: string) {
  return useQuery({ queryKey: ['survey', slug], queryFn: () => apiFetch<SurveyDetail>(surveyPath(slug)), enabled: !!slug, staleTime: 5_000, refetchInterval: 15_000, refetchOnWindowFocus: true });
}

/** The Invite preview — read only when a person opens it (looking queues nothing). */
export function useSurveyInvite(slug: string, enabled: boolean) {
  return useQuery({ queryKey: ['survey-invite', slug], queryFn: () => apiFetch<InvitePreview>(surveyPath(slug, '/invite')), enabled: !!slug && enabled, staleTime: 10_000 });
}

export function useSurveyPeople(q: string) {
  const query = q.trim();
  return useQuery({
    queryKey: ['survey-people', query],
    queryFn: () => apiFetch<{ people: SurveyPerson[] }>(`/api/surveys/people?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 1,
    staleTime: 5_000,
  });
}

/** A tenant's own answers (the "Surveys" line on their profile). */
export function usePersonSurveys(tenantId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['person-surveys', tenantId],
    queryFn: () => apiFetch<{ surveys: PersonSurvey[] }>(`/api/surveys/of/${encodeURIComponent(tenantId)}`),
    enabled: !!tenantId && enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export const pressSurvey = <T,>(slug: string, body: Record<string, unknown>) => apiFetch<T>(surveyPath(slug, '/press'), { method: 'POST', body: JSON.stringify(body) });
export const createSurvey = (body: SurveyInput) => apiFetch<SurveyDetail>('/api/surveys/new', { method: 'POST', body: JSON.stringify(body) });
export const editSurvey = (slug: string, body: SurveyInput) => apiFetch<SurveyDetail>(surveyPath(slug, '/edit'), { method: 'POST', body: JSON.stringify(body) });

/** "Careful translation" in the editor: the Spanish for the English boxes typed there (the assistant; nothing is saved). */
export const translateSurveyWords = (texts: string[]) => apiFetch<{ es: string[]; offline: boolean; remainingToday: number }>('/api/surveys/translate', { method: 'POST', body: JSON.stringify({ texts }) });

export type { EventSendResult };

/** After any press: the survey, its Invite preview and the list refresh. */
export function useSurveyRefresh() {
  const qc = useQueryClient();
  return (slug?: string) => {
    void qc.invalidateQueries({ queryKey: ['surveys'] });
    if (slug) {
      void qc.invalidateQueries({ queryKey: ['survey', slug] });
      void qc.invalidateQueries({ queryKey: ['survey-invite', slug] });
    }
  };
}
